import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errorMessage } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useI18n } from "../i18n";

const Login = () => {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(form);
      toast.success(`${t("auth.welcomeBack")}, ${data.user.name.split(" ")[0]}`);
      const target = data.user.role === "employee" ? "/me" : "/app/dashboard";
      navigate(location.state?.from?.startsWith("/app") && data.user.role === "admin" ? location.state.from : target, {
        replace: true,
      });
    } catch (err) {
      toast.error(errorMessage(err, "Could not sign in"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="ll-hero-glow relative hidden flex-col justify-between border-r p-10 lg:flex">
        <div className="ll-noise absolute inset-0 opacity-60" aria-hidden="true" />
        <Link to="/" className="relative flex items-center gap-2" data-testid="login-brand-link">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <BarChart3 className="h-4 w-4" />
          </span>
          <span className="font-display text-base font-semibold">LedgerLens</span>
        </Link>
        <div className="relative max-w-md">
          <h2 className="font-display text-3xl font-semibold tracking-tight">
            {t("auth.heroTitle")}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("auth.heroSub")}
          </p>
        </div>
        <p className="relative text-xs text-muted-foreground">
          {t("auth.heroFooter")}
        </p>
      </div>

      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex justify-end lg:hidden">
            <LanguageSwitcher />
          </div>
          <div className="mb-4 hidden justify-end lg:flex">
            <LanguageSwitcher />
          </div>
          <h1 className="font-display text-2xl font-semibold">{t("auth.loginTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("auth.loginSub")}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("auth.workEmail")}</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("common.password")}</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("common.signIn")}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("auth.newCompany")}{" "}
            <Link to="/signup" className="font-medium text-primary hover:underline" data-testid="login-signup-link">
              {t("auth.createAccount")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
